/* iter178 — Promo Campaigns admin tab.
   Scheduled rotating promotions for the single permanent /offer QR code.
   Lives inside the Promo Links page (`/admin/promos`) as the first tab.
*/
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import {
  Calendar, Plus, Pencil, Trash2, AlertTriangle, Tag, Sparkles, Users,
} from 'lucide-react';
import PromoEntriesDrawer from './PromoEntriesDrawer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CUSTOMER_TMPL =
  'Thanks {first_name}! NexFlow received your request for {promo_name}. Your promo code is {promo_code}. Our office will contact you shortly. For urgent service, call {call_number}. Reply STOP to opt out.';
const DEFAULT_OFFICE_TMPL =
  'New promo lead: {customer_name}, {phone}, {promo_name}, code {promo_code}, {need}, {urgency}.';

const blank = () => ({
  name: '',
  promo_code: '',
  start_date: '',
  end_date: '',
  active: true,
  priority: 100,
  headline: '',
  offer_text: '',
  call_number: '614-285-4509',
  intake_form_enabled: true,
  customer_confirmation_template: DEFAULT_CUSTOMER_TMPL,
  office_notification_template: DEFAULT_OFFICE_TMPL,
  office_sms_override: '',
  individual_list_name: '',
  internal_notes: '',
  campaign_type: 'offer',
  prize_value: '',
  fine_print: '',
  rules_text: '',
  rules_url: '',
});

const DEFAULT_GIVEAWAY_CUSTOMER_TMPL =
  "Thanks {first_name}! You're entered in the {promo_name}. Your entry code is {promo_code}. One winner will be selected monthly. NexFlow will contact you if you win. Reply STOP to opt out.";
const DEFAULT_GIVEAWAY_OFFICE_TMPL =
  'New giveaway entry: {customer_name}, {phone}, {promo_name}, code {promo_code}, interest: {need}.';

export const PromoCampaignsTab = () => {
  const [items, setItems] = useState([]);
  const [today, setToday] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  // iter187 — Campaign Entries drawer state.
  const [entriesFor, setEntriesFor] = useState(null); // { id, name }
  const [saving, setSaving] = useState(false);
  const [officeNumber, setOfficeNumber] = useState('');
  const [officeSaved, setOfficeSaved] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/promo-campaigns`, { withCredentials: true });
      setItems(data.campaigns || []);
      setToday(data.today || '');
      setActiveId(data.active_id || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load campaigns');
    }
    try {
      const { data } = await axios.get(`${API}/admin/office-sms-number`, { withCredentials: true });
      setOfficeNumber(data.number || '');
      setOfficeSaved(data.number || '');
    } catch (e) {
      // non-fatal
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveOfficeNumber = async () => {
    try {
      await axios.put(`${API}/admin/office-sms-number`, { number: officeNumber.trim() }, { withCredentials: true });
      setOfficeSaved(officeNumber.trim());
      toast.success('Office SMS number saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
  };

  const openCreate = () => setEditing({ ...blank(), _isNew: true });
  const openEdit = (c) => setEditing({ ...c });

  const save = async () => {
    if (!editing) return;
    const payload = {
      name: editing.name,
      promo_code: editing.promo_code,
      start_date: editing.start_date,
      end_date: editing.end_date,
      active: !!editing.active,
      priority: Number(editing.priority) || 100,
      headline: editing.headline || '',
      offer_text: editing.offer_text || '',
      call_number: editing.call_number || '',
      intake_form_enabled: editing.intake_form_enabled !== false,
      customer_confirmation_template: editing.customer_confirmation_template || '',
      office_notification_template: editing.office_notification_template || '',
      office_sms_override: editing.office_sms_override || '',
      individual_list_name: editing.individual_list_name || editing.name,
      internal_notes: editing.internal_notes || '',
      campaign_type: editing.campaign_type || 'offer',
      prize_value: editing.prize_value || '',
      fine_print: editing.fine_print || '',
      rules_text: editing.rules_text || '',
      rules_url: editing.rules_url || '',
    };
    if (!payload.name.trim()) return toast.error('Name is required');
    if (!payload.promo_code.trim()) return toast.error('Promo code is required');
    if (!payload.start_date || !payload.end_date) return toast.error('Start and end dates are required');
    if (payload.start_date > payload.end_date) return toast.error('Start date must be on or before end date');
    setSaving(true);
    try {
      if (editing._isNew) {
        await axios.post(`${API}/admin/promo-campaigns`, payload, { withCredentials: true });
        toast.success('Campaign created');
      } else {
        await axios.put(`${API}/admin/promo-campaigns/${editing.id}`, payload, { withCredentials: true });
        toast.success('Campaign saved');
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?\n\nThe per-campaign contact list will stay for history.`)) return;
    try {
      await axios.delete(`${API}/admin/promo-campaigns/${c.id}`, { withCredentials: true });
      toast.success('Campaign deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="promo-campaigns-tab" className="space-y-6">
      {/* Office SMS number */}
      <div data-testid="office-sms-number-card" className="bg-[#141414] border border-[#262626] rounded-md p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-0 flex-1">
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Office SMS Number (Global Default)</Label>
            <Input
              data-testid="office-sms-input"
              value={officeNumber}
              onChange={(e) => setOfficeNumber(e.target.value)}
              placeholder="+16145551234"
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1 max-w-xs font-mono"
            />
            <p className="text-[#737373] text-[11px] mt-1.5">
              Receives an SMS for every promo lead. A campaign can override this with its own number.
            </p>
          </div>
          <Button
            data-testid="office-sms-save"
            onClick={saveOfficeNumber}
            disabled={officeNumber === officeSaved}
            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
          >
            Save
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-['Outfit'] text-2xl font-bold text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#D4AF37]" /> Scheduled Campaigns
          </h3>
          <p className="text-[#A3A3A3] text-sm mt-1 max-w-3xl">
            Schedule rotating promotions on the same /offer QR code. The campaign whose date range
            contains <strong className="text-white">today</strong> wins; if multiple overlap, lowest priority number wins.
            {today && <> Today: <code className="text-[#D4AF37]">{today}</code></>}
          </p>
        </div>
        <Button data-testid="campaign-add" onClick={openCreate} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
          <Plus className="w-4 h-4 mr-1.5" /> New Campaign
        </Button>
      </div>

      {loading ? (
        <p className="text-[#A3A3A3]">Loading…</p>
      ) : items.length === 0 ? (
        <div data-testid="campaign-empty" className="bg-[#141414] border border-[#262626] rounded-md p-6 text-[#A3A3A3]">
          No scheduled campaigns yet. The evergreen offer is what shows on /offer until you add one.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div
              key={c.id}
              data-testid={`campaign-row-${c.id}`}
              className={`bg-[#141414] border rounded-md p-4 flex flex-wrap items-center gap-3 ${c.is_active_now ? 'border-[#D4AF37]' : 'border-[#262626]'}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-bold">{c.name}</p>
                  {c.is_active_now && (
                    <span data-testid={`campaign-active-now-${c.id}`} className="text-[10px] uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Live on /offer now
                    </span>
                  )}
                  {!c.active && (
                    <span className="text-[10px] uppercase tracking-wider text-[#737373] bg-[#262626] border border-[#3a3a3a] px-1.5 py-0.5 rounded">Inactive</span>
                  )}
                  {(c.overlaps_with && c.overlaps_with.length > 0) && (
                    <span data-testid={`campaign-overlap-warning-${c.id}`} className="text-[10px] uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/40 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Overlaps {c.overlaps_with.length}
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3] bg-[#0A0A0A] border border-[#262626] px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                    <Tag className="w-3 h-3" /> {c.promo_code}
                  </span>
                  <span
                    data-testid={`campaign-type-badge-${c.id}`}
                    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                      c.campaign_type === 'giveaway'
                        ? 'text-purple-200 bg-purple-500/15 border-purple-500/40'
                        : 'text-sky-200 bg-sky-500/15 border-sky-500/40'
                    }`}
                  >
                    {c.campaign_type === 'giveaway' ? 'Giveaway' : 'Offer'}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-[#A3A3A3]">
                    P{c.priority}
                  </span>
                </div>
                <p className="text-[#A3A3A3] text-xs mt-1">
                  {c.start_date} → {c.end_date}
                </p>
                <p className="text-[#525252] text-xs mt-1">
                  {c.lead_count || 0} leads · list: {c.individual_list_name || c.name}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  data-testid={`campaign-view-entries-${c.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => setEntriesFor({ id: c.id, name: c.name })}
                  className="border-[#3a3a3a] text-white hover:bg-[#262626]"
                  title="View everyone who entered this campaign"
                >
                  <Users className="w-4 h-4 mr-1" /> View Entries
                  {c.lead_count > 0 && (
                    <span className="ml-1 text-[10px] text-[#D4AF37] font-bold">{c.lead_count}</span>
                  )}
                </Button>
                <Button data-testid={`campaign-edit-${c.id}`} size="sm" onClick={() => openEdit(c)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
                  <Pencil className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button data-testid={`campaign-delete-${c.id}`} size="sm" variant="outline" onClick={() => remove(c)} className="border-red-500/40 text-red-300 hover:bg-red-500/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <SheetContent
          side="right"
          className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-xl overflow-y-auto"
          data-testid="campaign-edit-sheet"
        >
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#D4AF37]" />
              {editing?._isNew ? 'New Campaign' : `Edit "${editing?.name || ''}"`}
            </SheetTitle>
            <SheetDescription className="text-[#A3A3A3]">
              Scheduled within a date range. Active campaign auto-selected by today&apos;s date.
            </SheetDescription>
          </SheetHeader>

          {editing && (
            <div className="mt-6 space-y-4">
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Campaign Type *</Label>
                <div data-testid="campaign-type-picker" className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="campaign-type-offer"
                    onClick={() => {
                      const next = { ...editing, campaign_type: 'offer' };
                      // Switch default templates if admin hasn't customized.
                      if (next.customer_confirmation_template === DEFAULT_GIVEAWAY_CUSTOMER_TMPL || !next.customer_confirmation_template) {
                        next.customer_confirmation_template = DEFAULT_CUSTOMER_TMPL;
                      }
                      if (next.office_notification_template === DEFAULT_GIVEAWAY_OFFICE_TMPL || !next.office_notification_template) {
                        next.office_notification_template = DEFAULT_OFFICE_TMPL;
                      }
                      setEditing(next);
                    }}
                    className={`text-left text-sm px-3 py-3 rounded border transition-colors ${
                      (editing.campaign_type || 'offer') === 'offer'
                        ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-bold'
                        : 'bg-[#0A0A0A] text-white border-[#262626] hover:border-[#D4AF37]'
                    }`}
                  >
                    Offer / Service Promo
                    <span className="block text-[10px] opacity-80 mt-0.5 font-normal">Discount/coupon-style landing</span>
                  </button>
                  <button
                    type="button"
                    data-testid="campaign-type-giveaway"
                    onClick={() => {
                      const next = { ...editing, campaign_type: 'giveaway' };
                      if (next.customer_confirmation_template === DEFAULT_CUSTOMER_TMPL || !next.customer_confirmation_template) {
                        next.customer_confirmation_template = DEFAULT_GIVEAWAY_CUSTOMER_TMPL;
                      }
                      if (next.office_notification_template === DEFAULT_OFFICE_TMPL || !next.office_notification_template) {
                        next.office_notification_template = DEFAULT_GIVEAWAY_OFFICE_TMPL;
                      }
                      setEditing(next);
                    }}
                    className={`text-left text-sm px-3 py-3 rounded border transition-colors ${
                      editing.campaign_type === 'giveaway'
                        ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-bold'
                        : 'bg-[#0A0A0A] text-white border-[#262626] hover:border-[#D4AF37]'
                    }`}
                  >
                    Giveaway / Sweepstakes
                    <span className="block text-[10px] opacity-80 mt-0.5 font-normal">Lighter form · entry code · prize value</span>
                  </button>
                </div>
                {editing.campaign_type === 'giveaway' && (
                  <p className="text-amber-300 text-[11px] mt-2 leading-relaxed">
                    Public page will say <strong>Enter Giveaway</strong>, hide service-address fields, and require a rules acknowledgment.
                  </p>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Name *</Label>
                  <Input data-testid="campaign-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Fairfield County Fair" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Promo Code *</Label>
                  <Input data-testid="campaign-promo-code" value={editing.promo_code} onChange={(e) => setEditing({ ...editing, promo_code: e.target.value.toUpperCase() })} placeholder="FAIR2026" className="bg-[#0A0A0A] border-[#262626] text-white mt-1 font-mono" />
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Start Date *</Label>
                  <Input data-testid="campaign-start-date" type="date" value={editing.start_date} onChange={(e) => setEditing({ ...editing, start_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">End Date *</Label>
                  <Input data-testid="campaign-end-date" type="date" value={editing.end_date} onChange={(e) => setEditing({ ...editing, end_date: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Priority</Label>
                  <Input data-testid="campaign-priority" type="number" value={editing.priority} onChange={(e) => setEditing({ ...editing, priority: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
                  <p className="text-[#737373] text-[10px] mt-1">Lower number wins overlap.</p>
                </div>
              </div>
              <label className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] rounded p-3">
                <Switch data-testid="campaign-active" checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <div>
                  <p className="text-white text-sm font-bold">Active</p>
                  <p className="text-[#A3A3A3] text-[11px]">Inactive campaigns never show on /offer, even inside their date range.</p>
                </div>
              </label>
              <label className="flex items-center gap-3 bg-[#0A0A0A] border border-[#262626] rounded p-3">
                <Switch data-testid="campaign-intake-enabled" checked={editing.intake_form_enabled !== false} onCheckedChange={(v) => setEditing({ ...editing, intake_form_enabled: v })} />
                <div>
                  <p className="text-white text-sm font-bold">Show intake form</p>
                  <p className="text-[#A3A3A3] text-[11px]">If off, the landing page shows the call button only.</p>
                </div>
              </label>

              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Headline</Label>
                <Input data-testid="campaign-headline" value={editing.headline} onChange={(e) => setEditing({ ...editing, headline: e.target.value })} placeholder="Fairfield County Fair Special!" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Offer Text</Label>
                <Textarea data-testid="campaign-offer-text" rows={3} value={editing.offer_text} onChange={(e) => setEditing({ ...editing, offer_text: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Call Number</Label>
                  <Input data-testid="campaign-call-number" value={editing.call_number} onChange={(e) => setEditing({ ...editing, call_number: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Office SMS Override (optional)</Label>
                  <Input data-testid="campaign-office-sms-override" value={editing.office_sms_override} onChange={(e) => setEditing({ ...editing, office_sms_override: e.target.value })} placeholder="leave blank to use global" className="bg-[#0A0A0A] border-[#262626] text-white mt-1 font-mono" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Customer Confirmation SMS Template</Label>
                <Textarea data-testid="campaign-customer-template" rows={3} value={editing.customer_confirmation_template} onChange={(e) => setEditing({ ...editing, customer_confirmation_template: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1 font-mono text-xs" />
                <p className="text-[#737373] text-[10px] mt-1">
                  Tokens: <code>{'{first_name}'}</code> <code>{'{promo_name}'}</code> <code>{'{promo_code}'}</code> <code>{'{call_number}'}</code>
                </p>
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Office Notification SMS Template</Label>
                <Textarea data-testid="campaign-office-template" rows={3} value={editing.office_notification_template} onChange={(e) => setEditing({ ...editing, office_notification_template: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1 font-mono text-xs" />
                <p className="text-[#737373] text-[10px] mt-1">
                  Tokens: <code>{'{customer_name}'}</code> <code>{'{phone}'}</code> <code>{'{promo_name}'}</code> <code>{'{promo_code}'}</code> <code>{'{need}'}</code> <code>{'{urgency}'}</code>
                </p>
              </div>

              {editing.campaign_type === 'giveaway' && (
                <>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Prize Value</Label>
                      <Input
                        data-testid="campaign-prize-value"
                        value={editing.prize_value || ''}
                        onChange={(e) => setEditing({ ...editing, prize_value: e.target.value })}
                        placeholder="up to $1,800"
                        className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Rules URL (optional)</Label>
                      <Input
                        data-testid="campaign-rules-url"
                        value={editing.rules_url || ''}
                        onChange={(e) => setEditing({ ...editing, rules_url: e.target.value })}
                        placeholder="https://… (leave blank for inline text only)"
                        className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Fine Print</Label>
                    <Textarea
                      data-testid="campaign-fine-print"
                      rows={2}
                      value={editing.fine_print || ''}
                      onChange={(e) => setEditing({ ...editing, fine_print: e.target.value })}
                      placeholder="No purchase necessary. One winner selected monthly. Some restrictions apply."
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Rules Text (inline)</Label>
                    <Textarea
                      data-testid="campaign-rules-text"
                      rows={4}
                      value={editing.rules_text || ''}
                      onChange={(e) => setEditing({ ...editing, rules_text: e.target.value })}
                      className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                    />
                  </div>
                </>
              )}

              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Individual List Name</Label>
                <Input data-testid="campaign-list-name" value={editing.individual_list_name} onChange={(e) => setEditing({ ...editing, individual_list_name: e.target.value })} placeholder={editing.name || 'Fairfield County Fair 2026'} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Internal Notes</Label>
                <Textarea data-testid="campaign-notes" rows={2} value={editing.internal_notes} onChange={(e) => setEditing({ ...editing, internal_notes: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white mt-1" />
              </div>

              <div className="pt-2 flex items-center gap-2">
                <Button data-testid="campaign-save" onClick={save} disabled={saving} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold">
                  {saving ? 'Saving…' : 'Save Campaign'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="border-[#262626] text-white hover:bg-[#262626]">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* iter187 — Campaign Entries drawer */}
      <PromoEntriesDrawer
        open={!!entriesFor}
        onOpenChange={(o) => { if (!o) setEntriesFor(null); }}
        scope="campaign"
        id={entriesFor?.id}
        name={entriesFor?.name}
      />
    </div>
  );
};

export default PromoCampaignsTab;
